/** ORGANIZAREA DE ȘANTIER a soluției: documentul planificării
 *  (`cost_model/santier`, servit de control plane ca artefact
 *  `organizare_santier`), în COORDONATELE PLANULUI DE SITUAȚIE — aceleași
 *  perechi ca `geometry.poly` ale obiectelor lui, deci trec prin aceleași
 *  transformări (`punctInCadru` pe planșă, `inCadru` în scenă).
 *
 *  Aici: tipurile, cererea și cititorii puri pe săptămână (faza, macaralele
 *  cu fereastra deschisă, camioanele), cu teste în `santier.test.ts`. */
import { get } from "../../api";

export type Mutare = { faza: string; luni: string; zile: number; oameni: number; ce: string[]; nume: string };
export type ContainerSantier = { id: string; fel: "office" | "locker" | "canteen" | "wc" | "guard" | "first_aid"; etaj?: number; poly: number[][] };
export type DepozitSantier = {
  id: string; fel: "rebar" | "formwork" | "precast" | "materials" | "waste" | "concrete_pump";
  poly: number[][] | null; lung_m: number; lat_m: number; in_raza_macara?: boolean;
};
export type FazaSantier = {
  id: string; fel: "groapa" | "structuri" | "exterioare"; unitate: string | null;
  de_la: string; pana_la: string; motiv: string; oameni_varf: number; curte_m2: number;
  subsoluri: Record<string, "teren" | "groapa" | "placa">; corpuri_in_lucru: string[];
  blocuri_baraci: number[][][]; baraci: ContainerSantier[]; containere: { fel: string; numar: number }[]; etaje_baraci: number;
  depozite: DepozitSantier[]; mutare: Mutare | null;
};
export type MacaraSantier = {
  id: string; unitate: string; fel: "subsol" | "corp"; x: number; y: number; raza_m: number;
  de_la: string; pana_la: string; in_groapa: boolean; pe_placa: boolean; cota_baza_m: number;
  inaltime_carlig_m: number; rang_brat: number; acoperire: number; peste_vecini: string[]; peste_strada: string[];
};
export type SapaturaSantier = {
  unitate: string; de_la: number[]; spre: number[]; lungime_m: number; adancime_m: number; arie_m2: number;
  rampa: { id: string; poly: number[][] } | null; explicatie: string;
};
export type SaptamanaCamioane = {
  luni: string; pamant_m3: number; beton_m3: number; camioane_pamant: number; camioane_beton: number;
  camioane: number; zile_lucratoare: number; pe_zi: number;
};
export type OrganizareSantier = {
  versiune: number; cadru: string; start: string; sfarsit: string;
  lot: { arie_m2: number; perimetru_m: number; laturi: { id: string; fel: string; nume: string; lungime_m: number }[] };
  faze: FazaSantier[];
  gard: { axis: number[][]; inaltime_m: number; lungime_m: number; de_la: string; pana_la: string };
  porti: { id: string; fel: "vehicle" | "pedestrian"; axis: number[][]; latime_m: number; strada: string; de_la: string; pana_la: string }[];
  spalare_roti: { id: string; poly: number[][]; lungime_m: number; latime_m: number; de_la: string; pana_la: string };
  paza: { id: string; poly: number[][]; de_la: string; pana_la: string };
  drumuri: { id: string; fel: "temporary" | "ramp"; axis: number[][]; latime_m: number; sens: string; de_la: string; pana_la: string }[];
  macarale: MacaraSantier[];
  incap: { catarge: number; distanta_m: number; plafon_program: number };
  sapaturi: SapaturaSantier[];
  camioane: { pe_saptamana: SaptamanaCamioane[]; varf_pe_zi: number; surse: Record<string, string> };
  mutari: Mutare[]; avertismente: string[]; explicatii: string[];
  norme: { id: string; nivel: string; sursa: string; text: string }[];
};

export const getSantier = (projectId: string, solutionId: string, opt: { scenariu?: string | null; refa?: boolean } = {}) => {
  const q = new URLSearchParams();
  if (opt.scenariu) q.set("scenariu", opt.scenariu);
  if (opt.refa) q.set("refa", "1");
  const s = q.toString();
  return get<OrganizareSantier>(`/api/projects/${projectId}/solutions/${solutionId}/santier${s ? `?${s}` : ""}`);
};

/** Faza în care cade ziua `iso` (de luni): `de_la ≤ zi < pana_la`; înainte
 *  de prima fază, prima; după ultima, ultima. */
export function fazaLa(doc: OrganizareSantier, iso: string): FazaSantier | null {
  if (!doc.faze.length) return null;
  return doc.faze.find((f) => f.de_la <= iso && iso < f.pana_la) ?? (iso < doc.faze[0].de_la ? doc.faze[0] : doc.faze[doc.faze.length - 1]);
}

/** Macaralele cu fereastra deschisă în săptămâna de luni `iso` (duminica inclusiv). */
export function macaraleleLa(doc: OrganizareSantier, iso: string): MacaraSantier[] {
  const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + 6);
  const duminica = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return doc.macarale.filter((m) => m.de_la <= duminica && iso < m.pana_la);
}

/** Camioanele săptămânii, sau `null` când nu intră și nu iese nimic. */
export function camioaneleLa(doc: OrganizareSantier, iso: string): SaptamanaCamioane | null {
  return doc.camioane.pe_saptamana.find((w) => w.luni === iso) ?? null;
}

/** Gardul, porțile, spălarea și drumul stau cât ține organizarea provizorie. */
export function organizareaProvizorie(doc: OrganizareSantier, iso: string): boolean {
  return doc.gard.de_la <= iso && iso < doc.gard.pana_la;
}
